import type { ScriptDoc } from './config';
import { isScriptDoc } from './docShape';

const key = (entryId: string) => `script-draft:${entryId}`;

/** Parses a stored draft, accepting only a well-formed doc-JSON payload. */
function parseStoredDraft(raw: string): ScriptDoc | null {
  const parsed: unknown = JSON.parse(raw);
  if (!isScriptDoc(parsed)) return null;
  return parsed;
}

/** Local (browser) draft persistence for the script editor, keyed per roster
 *  entry. A thin seam — a server-backed implementation can replace it later
 *  without touching the editor or modal. Stores the editor's native doc-JSON.
 *
 *  The key predates the doc-JSON editor: earlier builds stored a markdown +
 *  style-token object under it. `load` treats any non-doc payload as no draft
 *  and removes it, so a stale entry can never crash the editor on open. */
export const draftStore = {
  load(entryId: string): ScriptDoc | null {
    try {
      const raw = localStorage.getItem(key(entryId));
      if (!raw) return null;
      const draft = parseStoredDraft(raw);
      if (!draft) draftStore.clear(entryId);
      return draft;
    } catch {
      draftStore.clear(entryId);
      return null;
    }
  },
  save(entryId: string, doc: ScriptDoc): void {
    try {
      localStorage.setItem(key(entryId), JSON.stringify(doc));
    } catch {
      // Quota exceeded or storage disabled (private mode): drafting degrades to
      // in-memory only, matching load()'s silent failure.
    }
  },
  clear(entryId: string): void {
    try {
      localStorage.removeItem(key(entryId));
    } catch {
      // Storage disabled — nothing to clear.
    }
  },
};
